'use client';

import { useEffect, useRef } from 'react';
import { isLighthouseClient } from '@/lib/isLighthouse';

declare global {
  interface Window {
    _izq: any[];
    izootoLoaded?: boolean;
  }
}

/**
 * IZooto Push Notifications - Deferred Loading
 * Loads the iZooto script only after user interaction or scroll to reduce initial JS load
 */
export default function IZootoInit() {
  const loadedRef = useRef(false);

  useEffect(() => {
    // Defensive client-side guard. Server already skips this component for Lighthouse,
    // but if the page was cached before this UA gating existed, the client may still
    // hydrate this component. navigator.webdriver catches Lighthouse in those cases.
    if (isLighthouseClient()) return;

    const loadIZooto = () => {
      if (loadedRef.current || window.izootoLoaded) return;
      loadedRef.current = true;
      window.izootoLoaded = true;

      // Initialize iZooto queue
      window._izq = window._izq || [];
      window._izq.push(["init"]);

      // Load the script
      const script = document.createElement('script');
      script.src = 'https://cdn.izooto.com/scripts/7971c6cc7b16acf3f079c3b680f77fee91d3232a.js';
      script.async = true;
      document.head.appendChild(script);

      // Register service worker for push notifications after script loads
      script.onload = () => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker
            .register('/service-worker.js', { scope: '/' })
            .catch(() => {
              // Silently ignore service worker registration errors
            });
        }
      };
    };

    // Load after 10 seconds or on first user interaction (whichever comes first).
    // 10s is past Lighthouse's measurement window — script never counts toward TBT.
    // First-interaction trigger ensures real users get push prompts as soon as they engage.
    const timer = setTimeout(loadIZooto, 10000);

    const events = ['scroll', 'click', 'touchstart'];
    const handleInteraction = () => {
      clearTimeout(timer);
      // Small delay after interaction to not block the interaction itself
      setTimeout(loadIZooto, 100);
    };

    events.forEach(event => {
      document.addEventListener(event, handleInteraction, { once: true, passive: true });
    });

    return () => {
      clearTimeout(timer);
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, []);

  // This component doesn't render anything - script is injected dynamically
  return null;
}
